import { useEffect, useState } from "react";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import Table from "../../components/Table/index";
import { DEV_URL, rget } from "../../utils/requests";
import loader from "../../assets/images/datahub-loader.svg";
import { ColumnDef } from "@tanstack/table-core";

interface ViewLineItemModalProps {
  show: boolean;
  lineItem: any;
  onClose: () => void;
}

interface ILineItemUsageDetail {
  accountNo: string;
  usageFrom: string;
  usageUpTo: string;
  usage: number;
  uom: string;
  billCycle: string;
  meterDemandKW: number;
  meterDemandKVA: number;
  powerFactor: number;
  utilityCharges: number | null;
  commodityId: number;
}

const ViewLineItemModal = ({
  show,
  lineItem,
  onClose,
}: ViewLineItemModalProps) => {
  const [usageData, setUsageData] = useState<ILineItemUsageDetail[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const accountNo = lineItem?.accountNo;
  const commodityId = lineItem?.commodityId;

  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    return value;
  };

  useEffect(() => {
    if (!show || !accountNo) return;

    const getUsageByAccount = async () => {
      try {
        setLoading(true);

        const response = await rget(
          `${DEV_URL}/api/HistoricalUsage/by-account/${accountNo}?commodityId=${commodityId}`
        );

        setUsageData(response?.data?.items || []);
      } catch (error) {
        console.error("Error fetching account usage detail:", error);
        setUsageData([]);
      } finally {
        setLoading(false);
      }
    };

    getUsageByAccount();
  }, [show, accountNo, commodityId]);

  const columns: ColumnDef<ILineItemUsageDetail>[] = [
    {
  header: "Account No",
  accessorKey: "accountNo",
  cell: (props: any) => {
    const isElectric = props.row.original.commodityId === 1;
    const icon = isElectric
      ? "mdi mdi-lightning-bolt text-warning"
      : "mdi mdi-fire text-success";
    return (
      <>
        <i className={`${icon} me-1`} />
        {props.row.original.accountNo}
      </>
    );
  },
},
    {
      header: "Start",
      accessorKey: "usageFrom",
      cell: ({ getValue }: any) => formatValue(getValue()),
    },
    {
      header: "End",
      accessorKey: "usageUpTo",
      cell: ({ getValue }: any) => formatValue(getValue()),
    },
    {
      header: "Usage",
      accessorKey: "usage",
      cell: ({ getValue }: any) => formatValue(getValue()),
    },
    {
      header: "UOM",
      accessorKey: "uom",
      cell: ({ getValue }: any) => formatValue(getValue()),
    },
    {
      header: "Bill Cycle",
      accessorKey: "billCycle",
      cell: ({ getValue }: any) => formatValue(getValue()),
    },
    {
      header: "Metered Demand(KW)",
      accessorKey: "meterDemandKW",
      cell: ({ getValue }: any) => formatValue(getValue()),
    },
    {
      header: "Metered Demand(KVA)",
      accessorKey: "meterDemandKVA",
      cell: ({ getValue }: any) => formatValue(getValue()),
    },
    {
      header: "Power Factor",
      accessorKey: "powerFactor",
      cell: ({ getValue }: any) => formatValue(getValue()),
    },
  ];

  return (
    <ConfirmationModal
      dialogClassName="modal-md"
      show={show}
      onHide={onClose}
      centered
      size="xl"
      title="Historical Usage"
      confirmButtonLabel="Close"
      onConfirm={onClose}
    >
        {loading ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: "200px" }}
          >
            <img src={loader} height={44} alt="Loading" />
          </div>
        ) : (
          <Table<ILineItemUsageDetail>
            columns={columns}
            data={usageData}
            enableSearch={false}
            enablePagination={false}
            enableHideColumns={false}
            enableSorting={false}
            enableFilters={false}
          />
        )}
    </ConfirmationModal>
  );
};

export default ViewLineItemModal;
